import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { Class } from '../models/Class';
import { useLazyLoadQuery } from 'react-relay';
import {
  ClassesGetAllBasicDataQuery,
  ClassesGetAllBasicDataQuery$data,
} from '../graphQLOperations/__generated__/ClassesGetAllBasicDataQuery.graphql';
import {
  allClasses,
  allClassesBasicData,
  classesByTeacherId,
  classesByTeacherIdOnlyClasses,
} from '../graphQLOperations/Classes';
import {
  ClassesGetByTeacherIdOnlyClassesQuery,
  ClassesGetByTeacherIdOnlyClassesQuery$data,
} from '../graphQLOperations/__generated__/ClassesGetByTeacherIdOnlyClassesQuery.graphql';
import {
  ClassesGetAllQuery,
  ClassesGetAllQuery$data,
} from '../graphQLOperations/__generated__/ClassesGetAllQuery.graphql';
import {
  ClassesGetByTeacherIdQuery,
  ClassesGetByTeacherIdQuery$data,
} from '../graphQLOperations/__generated__/ClassesGetByTeacherIdQuery.graphql';
import { isUserAdmin } from '../businessLogicService/isUserAdmin';
import { useHiddenClassIds } from './useHiddenClassIds';

export const useGetClassesByRole = (
  onlyClasses = false,
  storeOrNetworkPolicy = false,
  classHasStudents: boolean | null = null,
  includeHidden = false,
) => {
  const userRedux = useAppSelector(selectUser);
  // Hidden classes are excluded from every consumer (dropdowns) except callers
  // that opt in with includeHidden (the Classes & Rosters page's hidden section).
  const hiddenClassIds = useHiddenClassIds(includeHidden);
  const [refetchKey, setRefetchKey] = useState(0);
  const [allClassesData, setAllClassesData] = useState<Class[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [allClassesLoaded, setAllClassesLoaded] = useState<boolean>(false);
  const isCurrentUserAdmin = isUserAdmin(userRedux);
  const currentClassesFilter = {
    haveAtLeastOneStudent: classHasStudents,
    isActive: 'ACTIVE' as const,
    siteIsActive: 'ACTIVE' as const,
  };

  const classesData = isCurrentUserAdmin
    ? onlyClasses
      ? (useLazyLoadQuery<ClassesGetAllBasicDataQuery>(
          allClassesBasicData,
          {
            filter: currentClassesFilter,
            cursor,
          },
          {
            fetchKey: `${cursor || ''}_${refetchKey}`,
            fetchPolicy: storeOrNetworkPolicy ? 'store-or-network' : 'network-only',
          },
        ) as ClassesGetAllBasicDataQuery$data)
      : (useLazyLoadQuery<ClassesGetAllQuery>(
          allClasses,
          {
            filter: currentClassesFilter,
            cursor,
          },
          {
            fetchKey: `${cursor || ''}_${refetchKey}`,
            fetchPolicy: storeOrNetworkPolicy ? 'store-or-network' : 'network-only',
          },
        ) as ClassesGetAllQuery$data)
    : onlyClasses
    ? (useLazyLoadQuery<ClassesGetByTeacherIdOnlyClassesQuery>(
        classesByTeacherIdOnlyClasses,
        {
          filter: currentClassesFilter,
          teacherId: 'UserNode:' + userRedux?.id,
        },
        { fetchKey: refetchKey, fetchPolicy: storeOrNetworkPolicy ? 'store-or-network' : 'network-only' },
      ) as ClassesGetByTeacherIdOnlyClassesQuery$data)
    : (useLazyLoadQuery<ClassesGetByTeacherIdQuery>(
        classesByTeacherId,
        {
          filter: currentClassesFilter,
          teacherId: 'UserNode:' + userRedux?.id,
        },
        { fetchKey: refetchKey, fetchPolicy: storeOrNetworkPolicy ? 'store-or-network' : 'network-only' },
      ) as ClassesGetByTeacherIdQuery$data);

  useEffect(() => {
    if (isCurrentUserAdmin) {
      const typedClassesData = classesData as ClassesGetAllQuery$data;

      if (typedClassesData && typedClassesData?.classes?.nodes?.length) {
        const previousClasses = allClassesData || [];
        const newClasses = typedClassesData?.classes?.nodes || [];
        const data = [...previousClasses, ...newClasses];

        setAllClassesData(data as Class[]);
        setCursor(typedClassesData.classes.pageInfo.endCursor || '');
      } else {
        setAllClassesLoaded(true);
      }
    } else {
      const typedClassesData = classesData as ClassesGetByTeacherIdQuery$data;

      setAllClassesData(typedClassesData?.classesByTeacherId as Class[]);
      setAllClassesLoaded(true);
    }
  }, [classesData]);

  const visibleClassesData = useMemo(
    () => (includeHidden ? allClassesData : (allClassesData ?? []).filter(cls => cls && !hiddenClassIds.has(cls.id))),
    [includeHidden, allClassesData, hiddenClassIds],
  );

  const refetch = useCallback(() => {
    setAllClassesData([]);
    setAllClassesLoaded(false);
    setCursor(null);
    setRefetchKey(key => key + 1);
  }, []);

  return { allClassesLoaded, allClassesData: visibleClassesData, setRefetchKey, refetch };
};
